import React, { Component } from 'react';
import { ColorPicker } from '../../components';
import PreferenceRequiredInteraction from "../../components/preference-required-interaction/preference-required-interaction";
import RDP_CONFIG from '../../config';
import './element-math-formula-properties.css';

export default class ElementMathFormulaProperties extends Component {
  constructor(props) {
    super(props);

    this.state = {
      content: this.props.content,
      previewFormula: this.props.content.formula || '',
      generatingAccessibleText: false,
      hasAIConnector: false
    };

    this.commonFormulas = [
      { name: 'Equação Quadrática', formula: 'x = \\frac{-b \\pm \\sqrt{b^2-4ac}}{2a}' },
      { name: 'Teorema de Pitágoras', formula: 'a^2 + b^2 = c^2' },
      { name: 'Integral', formula: '\\int_{a}^{b} f(x) dx' },
      { name: 'Derivada', formula: '\\frac{d}{dx}f(x) = \\lim_{h \\to 0} \\frac{f(x+h) - f(x)}{h}' },
      { name: 'Somatório', formula: '\\sum_{i=1}^{n} x_i' },
      { name: 'Limite', formula: '\\lim_{x \\to \\infty} f(x)' },
      { name: 'Matriz', formula: '\\begin{pmatrix} a & b \\\\ c & d \\end{pmatrix}' },
      { name: 'Fração', formula: '\\frac{a}{b}' },
      { name: 'Raiz Quadrada', formula: '\\sqrt{x}' },
      { name: 'Exponencial', formula: 'e^{x}' }
    ];
  }

  onContentTitleChange = (e) => {
    const content = { ...this.state.content };
    content.content_title = e.currentTarget.value !== "" ? e.currentTarget.value : content.content_title;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  onFormulaChange = (e) => {
    const formula = e.currentTarget.value;
    const content = { ...this.state.content };
    content.formula = formula;
    
    this.setState({ 
      content,
      previewFormula: formula
    }, () => {
      this.props.onContentChange(content);
    });
  };

  onDisplayModeChange = (e) => {
    const content = { ...this.state.content };
    content.display_mode = e.target.value;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  onBackgroundColorChange = (color) => {
    const content = { ...this.state.content };
    content.background_color = color;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  onFormulaColorChange = (color) => {
    const content = { ...this.state.content };
    content.formula_color = color;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  onFontSizeChange = (e) => {
    const content = { ...this.state.content };
    content.formula_font_size = e.currentTarget.value + 'px';
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  onPaddingTopChange = (e) => {
    const content = { ...this.state.content };
    content.padding_top = e.currentTarget.value !== "" ? parseInt(e.currentTarget.value) : content.padding_top;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  onPaddingBottomChange = (e) => {
    const content = { ...this.state.content };
    content.padding_bottom = e.currentTarget.value !== "" ? parseInt(e.currentTarget.value) : content.padding_bottom;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  insertCommonFormula = (formula) => {
    const content = { ...this.state.content };
    content.formula = formula;
    
    this.setState({ 
      content,
      previewFormula: formula
    }, () => {
      this.props.onContentChange(content);
    });
  };

  componentDidMount() {
    this.checkAIConnector();
  }

  checkAIConnector = () => {
    try {
      const project = JSON.parse(window.localStorage.getItem("rdp-composer-active-project"));
      const connectors = project && project.connectors;
      const hasAI = connectors && connectors.some(c => c.features && c.features.includes('IConnectorGenerativeIA'));
      this.setState({ hasAIConnector: hasAI });
    } catch (err) {
      console.log(err);
    }
  };

  onAccessibleDescriptionChange = (e) => {
    const content = { ...this.state.content };
    content.accessible_description = e.currentTarget.value;
    
    this.setState({ content }, () => {
      this.props.onContentChange(content);
    });
  };

  generateAccessibleText = async () => {
    const { content } = this.state;
    
    if (!content.formula || content.formula.trim() === '') {
      alert('Por favor, insira uma fórmula LaTeX antes de gerar a descrição acessível.');
      return;
    }

    this.setState({ generatingAccessibleText: true });

    try {
      const project = JSON.parse(window.localStorage.getItem("rdp-composer-active-project"));
      const userData = JSON.parse(window.localStorage.getItem("rdp-composer-user-data"));
      const connectors = project && project.connectors;
      const aiConnector = connectors && connectors.find(c => c.features && c.features.includes('IConnectorGenerativeIA'));

      if (!aiConnector) {
        alert('Nenhum conector de IA encontrado no projeto.');
        this.setState({ generatingAccessibleText: false });
        return;
      }

      const prompt = `Você é um assistente especializado em acessibilidade. Descreva a seguinte fórmula matemática em LaTeX de forma que uma pessoa com deficiência visual possa compreendê-la perfeitamente através de um leitor de tela. Use linguagem natural, clara e detalhada, explicando cada elemento matemático de forma sequencial e compreensível quando ouvida. Fórmula LaTeX: ${content.formula}. Retorne APENAS a descrição em texto corrido, sem formatação, símbolos especiais ou marcações.`;

      const response = await fetch(`https://${RDP_CONFIG.domain}/insights/v1.1/generative/connector/uid/${aiConnector.uid}/response`, {
        headers: {
          'Authorization': `${userData.type} ${userData.access_token}`,
          'Project-Key': RDP_CONFIG.projectKey,
          'Api-Env': RDP_CONFIG.apiEnv,
          'Content-Type': 'application/json'
        },
        method: 'POST',
        body: JSON.stringify({ prompt })
      });

      const text = await response.text();
      let data;
      
      try {
        data = JSON.parse(text);
      } catch (e) {
        data = { response: text };
      }
      
      if (data.response) {
        const updatedContent = { ...content, accessible_description: data.response };
        this.setState({ content: updatedContent, generatingAccessibleText: false }, () => {
          this.props.onContentChange(updatedContent);
        });
      } else {
        throw new Error('Resposta inválida da IA');
      }
    } catch (error) {
      console.error('Erro ao gerar descrição acessível:', error);
      alert('Erro ao gerar descrição acessível. Por favor, tente novamente.');
      this.setState({ generatingAccessibleText: false });
    }
  };

  render() {
    const { content } = this.state;
    const { labels } = this.props;

    return (
      <div className="rdp-element-math-formula-properties">
        <ul className="rdp-composer-element-edit-tabs">
          <li className="rdp-composer-element-edit-tabs-item active">Configurações</li>
        </ul>
        
        <div className="rdp-composer-element-edit-body">
          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_content_title || "Título do Conteúdo"}:</span>
          <input 
            type="text" 
            className="rdp-composer-element-edit-input" 
            onChange={this.onContentTitleChange} 
            defaultValue={content.content_title}
          />

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_latex_formula || "Fórmula LaTeX"}:</span>
          <textarea 
            className="rdp-composer-element-edit-textarea formula-input"
            onChange={this.onFormulaChange} 
            value={content.formula || ''}
            placeholder={labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.content_placeholder_formula || "Digite sua fórmula em LaTeX (ex: x^2 + y^2 = z^2)"}
            rows="4"
          />

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_accessible_description || "Descrição Acessível (para leitores de tela)"}:</span>
          <textarea 
            className="rdp-composer-element-edit-textarea"
            onChange={this.onAccessibleDescriptionChange} 
            value={content.accessible_description || ''}
            placeholder={labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.content_placeholder_accessible_description || "Descrição em linguagem natural da fórmula"}
            rows="3"
          />
          {this.state.hasAIConnector && (
            <button 
              className="rdp-composer-element-edit-button"
              onClick={this.generateAccessibleText}
              disabled={this.state.generatingAccessibleText || !content.formula}
              style={{ 
                padding: '8px 16px',
                backgroundColor: this.state.generatingAccessibleText ? '#ccc' : '#6f42c1',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: this.state.generatingAccessibleText || !content.formula ? 'not-allowed' : 'pointer',
                fontSize: '14px',
                marginBottom: '35px',
                width: '100%'
              }}
              title={!content.formula ? 'Insira uma fórmula primeiro' : 'Gerar descrição usando IA'}
            >
              {this.state.generatingAccessibleText ? '⏳ Gerando...' : '🤖 Gerar com IA'}
            </button>
          )}

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_common_formulas || "Fórmulas Comuns"}:</span>
          <div className="common-formulas-grid" style={{ marginTop: '10px', marginBottom: '20px' }}>
            {this.commonFormulas.map((item, index) => (
              <button 
                key={index}
                className="common-formula-btn"
                onClick={() => this.insertCommonFormula(item.formula)}
                title={item.formula}
              >
                {item.name}
              </button>
            ))}
          </div>

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_display_mode || "Modo de Exibição"}:</span>
          <select 
            defaultValue={content.display_mode || 'block'} 
            className="rdp-composer-element-edit-select" 
            onChange={this.onDisplayModeChange}
          >
            <option value="block">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_option_display_block || "Bloco (centralizado)"}</option>
            <option value="inline">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_option_display_inline || "Inline (na linha)"}</option>
          </select>

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_background_color || "Cor de Fundo"}:</span>
          <ColorPicker 
            labels={this.props.labels} 
            color={content.background_color || '#FFFFFF'} 
            onChange={this.onBackgroundColorChange} 
          />

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_formula_color || "Cor da Fórmula"}:</span>
          <ColorPicker 
            labels={this.props.labels} 
            color={content.formula_color || '#000000'} 
            onChange={this.onFormulaColorChange} 
          />

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_font_size || "Tamanho da Fonte"}:</span>
          <input 
            type="number" 
            className="rdp-composer-element-edit-input px" 
            onChange={this.onFontSizeChange} 
            defaultValue={parseInt(content.formula_font_size) || 20}
            min="12"
            max="48"
          />

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_margin_top || "Margem Superior"}:</span>
          <input 
            type="number" 
            className="rdp-composer-element-edit-input px" 
            onChange={this.onPaddingTopChange} 
            defaultValue={content.padding_top}
          />

          <span className="rdp-composer-element-edit-label">{labels && labels.elements && labels.elements.settings && labels.elements.settings.element_math_formula && labels.elements.settings.element_math_formula.settings_label_margin_bottom || "Margem Inferior"}:</span>
          <input 
            type="number" 
            className="rdp-composer-element-edit-input px" 
            onChange={this.onPaddingBottomChange} 
            defaultValue={content.padding_bottom}
          />

          <PreferenceRequiredInteraction
            title={(labels && labels.elements && labels.elements.settings && labels.elements.settings.preference_required_interaction && labels.elements.settings.preference_required_interaction.title) || "Interação Obrigatória"}
            label={(labels && labels.elements && labels.elements.settings && labels.elements.settings.preference_required_interaction && labels.elements.settings.preference_required_interaction.label) || "Exigir interação"}
            labels={labels}
            content={content}
            onContentChange={c => {
              this.setState({ content: c });
              this.props.onContentChange(c);
            }}
          />
        </div>
      </div>
    );
  }
}